// src/Pages/DraftsBatchReview.jsx
//
// T2a — surface `approved_pending_connection` on the token review page.
//
// approveViaToken's JSON branch already returns everything needed:
//   { status: 'approved_pending_connection', platform, pendingConnection: true }
// and getbatchdrafts already returns status and platform for drafts approved
// through an earlier visit. Both were being dropped: handleAction hardcoded
// 'approved', the settled card had no branch for it, and with no scheduledAt
// or publishedAt it printed a green tick and nothing else.
//
// Observed 2026-08-31: an owner approved 8 of 8, four went to
// approved_pending_connection, and the page showed eight green ticks followed
// by "All Done! 🎉". As far as they know, four posts went live.
//
// ALSO FIXED HERE: the completion redirect never fired on a cold load. It was
// scheduled inside handleAction, which only runs when a card is clicked — so
// a page opened after the batch was already finished set allDone from
// json.allReviewed, rendered "Redirecting to your business page..." and span
// forever. It now fires from an effect on the allDone transition, so it works
// however the page reaches that state.
//
// T2b — indicate BEFORE approving that a platform is not connected.
//
// getbatchdrafts now returns connectedPlatforms, missingPlatforms and a
// per-draft needsConnection (computed server-side from the same
// requiresSocialAccount the scheduler uses). The page had no way to know any
// of this, so the surface used by owners who never log in — and therefore the
// ones most likely to have nothing connected — said nothing until after they
// had already approved.
//
// The copy deliberately does NOT warn people off approving. Approve is the M6
// signal the whole learning system runs on, and "this won't publish" reads as
// "don't bother". It says what is true instead: approve it, and it goes out
// when you connect.

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams }                                 from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Paper,
  Button, Chip, Divider, Alert, AlertTitle, LinearProgress, TextField,
  Link as MuiLink
} from '@mui/material';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import CancelIcon       from '@mui/icons-material/Cancel';
import ThumbUpIcon      from '@mui/icons-material/ThumbUp';
import ThumbDownIcon    from '@mui/icons-material/ThumbDown';
import ArticleIcon      from '@mui/icons-material/Article';
import EditIcon         from '@mui/icons-material/Edit';
import SaveIcon         from '@mui/icons-material/Save';
import RestoreIcon      from '@mui/icons-material/Restore';
import LinkOffIcon      from '@mui/icons-material/LinkOff';

// ===== URL construction — matches existing unauth pattern =====
const proxyPrefix   = process.env.REACT_APP_PROXY_PREFIX      || '';
const baseApi       = process.env.REACT_APP_API_BASE_URL       || '';
const unauthBaseUrl = proxyPrefix + baseApi + 'unauth';

const APP_URL = process.env.REACT_APP_APP_URL || 'https://www.revfer.com';

const MAX_CONTENT_LENGTH = 20000;   // mirrors editviatoken.js

const PENDING_CONNECTION = 'approved_pending_connection';

// How long the completion screen stays up before leaving. Long enough to read
// one sentence; the blocked variant never redirects at all.
const REDIRECT_DELAY_MS = 2000;

// ===== Draft type colors =====
const DRAFT_COLORS = {
  social_post:     { bg: '#E3F2FD', color: '#1565C0' },
  review_response: { bg: '#E8F5E9', color: '#2E7D32' },
  faq:             { bg: '#FFF3E0', color: '#E65100' },
  blog_post:       { bg: '#F3E5F5', color: '#6A1B9A' },
  bio:             { bg: '#FCE4EC', color: '#880E4F' },
};

const PLATFORM_LABEL = {
  facebook:  'Facebook',
  instagram: 'Instagram',
  linkedin:  'LinkedIn',
  twitter:   'Twitter',
  google:    'Google',
};

const platformName = (p) => PLATFORM_LABEL[p] || p || 'social';

// One Facebook connection stores the Page AND its linked Instagram Business
// account (completePageSelection), so both platforms clear in one action.
// Say "account" rather than listing each one as a separate chore.
const platformList = (platforms) => {
  const names = platforms.map(platformName);
  if (names.length <= 1) return names[0] || 'social';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

// sendbatchnotification signs a SLUG into the token, but getbatchdrafts
// returns the raw businessName from the database, so building a URL from it
// yields unencoded spaces and ampersands. Mirror the slug transform here so
// this link is correct regardless of which name arrives. (Preferring
// payload.businessName server-side is T2c.)
const slugify = (name) =>
  String(name || '').replace(/[\s,.]+/g, '-').toLowerCase();

// Prefer the slug getbatchdrafts now returns from the token payload; fall
// back to slugifying the display name for tokens issued before that shipped.
const businessUrl = (businessId, businessName, businessSlug) =>
  businessId
    ? `${APP_URL}/business/${businessId}/${encodeURIComponent(businessSlug || slugify(businessName))}`
    : APP_URL;

// Where "connect your account" goes. A GUARDED route (App.js), so a logged-out
// owner hits RouteGuard, signs in, and is returned here — rather than landing
// on the public business view, which has no connect controls at all.
const connectAccountsUrl = (businessId) =>
  businessId ? `${APP_URL}/business/${businessId}/social/accounts` : APP_URL;

// ===== Single Draft Card =====
const DraftCard = ({ draft, token, businessId, businessName, businessSlug, onAction }) => {
  const [loading, setLoading] = useState(null); // 'approve' | 'reject' | null
  const [error, setError]     = useState('');
  // Holds the real status the server returned, not a hardcoded label, so
  // approved_pending_connection survives to the render below.
  const [result, setResult]   = useState(null);
  const [resultPlatform, setResultPlatform] = useState(null);

  // ===== edit state =====
  const [editing, setEditing]       = useState(false);
  const [editText, setEditText]     = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');
  // Live edited text. draft.content is the pristine original and is never
  // overwritten — Restore needs it verbatim for the server-side revert check.
  const [edited, setEdited] = useState(draft.editedContent || null);

  const original = draft.content || '';
  const shown    = edited ?? original;

  const colors = DRAFT_COLORS[draft.draftType] || { bg: '#F5F5F5', color: '#424242' };

  // PUT /v2/drafts/token/edit — does NOT consume the token, does NOT approve
  const putEdit = async (text) => {
    const res = await fetch(
      `${unauthBaseUrl}/v2/drafts/token/edit?token=${token}&draftId=${draft.draftId}`,
      {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ editedContent: text }),
      }
    );
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.message || 'Failed to save changes');
      err.status = res.status;
      throw err;
    }
    return data;   // { success, draftId, edited, message }
  };

  const handleSaveEdit = async () => {
    const text = editText;
    if (!text.trim()) {
      setEditError('Content cannot be empty');
      return;
    }
    if (text.length > MAX_CONTENT_LENGTH) {
      setEditError(`Too long — ${text.length} of ${MAX_CONTENT_LENGTH} characters`);
      return;
    }
    setEditError('');
    setEditSaving(true);
    try {
      const data = await putEdit(text);
      // edited:false = server saw the original text and cleared the edit
      setEdited(data.edited ? text : null);
      setEditing(false);
    } catch (e) {
      if (e.status === 409) {
        setEditError('This draft has already been reviewed.');
        setEditing(false);
      } else if (e.status === 401) {
        setEditError('This link has expired. Please open the most recent email or text.');
      } else {
        setEditError(e.message);
      }
    } finally {
      setEditSaving(false);
    }
  };

  // Restore = send the original back; server unsets editedContent
  const handleRestore = async () => {
    setEditError('');
    setEditSaving(true);
    try {
      await putEdit(original);
      setEdited(null);
      setEditing(false);
    } catch (e) {
      setEditError(e.status === 401
        ? 'This link has expired. Please open the most recent email or text.'
        : e.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleAction = async (action) => {
    setError('');
    setLoading(action);
    try {
      const endpoint = action === 'approve'
        ? `${unauthBaseUrl}/v2/drafts/token/approve`
        : `${unauthBaseUrl}/v2/drafts/token/reject`;

      const res = await fetch(
        `${endpoint}?token=${token}&draftId=${draft.draftId}`,
        { method: 'GET', headers: { 'Accept': 'application/json' } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Failed to ${action} draft`);

      // Trust the server's status. 'scheduled' and
      // 'approved_pending_connection' are both approvals but they end
      // differently, and only one of them results in a published post.
      const status = data.status
        || (action === 'approve' ? 'scheduled' : 'rejected');

      setResult(status);
      setResultPlatform(data.platform || null);
      onAction(draft.draftId, status, data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  // ===== Already reviewed — no Edit affordance (endpoint would 409) =====
  if (result || draft.reviewed || draft.status !== 'pending_approval') {
    const finalStatus = result || draft.status;

    // A draft approved earlier may since have PUBLISHED, in which case status
    // is 'published' — not 'approved'. The previous two-way check treated
    // anything that was not 'approved' as rejected, so a live post displayed
    // as "Rejected" on this page.
    const isRejected  = finalStatus === 'rejected';
    const isPublished = !!draft.publishedAt || finalStatus === 'published';

    // The owner approved, and nothing will publish until an account is
    // connected. flushPendingConnectionDrafts picks these up the moment one
    // is, so this is a wait, not a failure.
    const isPendingConnection = finalStatus === PENDING_CONNECTION;
    const platform = resultPlatform || draft.platform;

    const statusChip = isRejected
      ? { icon: <CancelIcon />,      label: 'Rejected',  color: 'error' }
      : isPendingConnection
        ? { icon: <LinkOffIcon />,   label: 'Approved',  color: 'warning' }
        : { icon: <CheckCircleIcon />,
            label: isPublished ? 'Published' : 'Approved', color: 'success' };

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 2,
          opacity: isPendingConnection ? 1 : 0.8,
          ...(isPendingConnection && {
            borderColor: 'warning.main',
            bgcolor:     '#FFFDF7',
          }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
          <Chip
            label={draft.label}
            size="small"
            sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 600 }}
          />
          <Chip
            icon={statusChip.icon}
            label={statusChip.label}
            size="small"
            color={statusChip.color}
            variant="outlined"
          />
        </Box>

        {/* The gap this task closes. Without it an owner sees a tick and
            assumes the post went out. */}
        {isPendingConnection && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#8D6E00', fontWeight: 600 }}>
              Not published yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              This post is ready to go. Connect your {platformName(platform)} account
              and Revfer will publish it for you.{' '}
              <MuiLink
                href={connectAccountsUrl(businessId)}
                underline="always"
                sx={{ fontWeight: 600 }}
              >
                Log in and connect {platformName(platform)}
              </MuiLink>
            </Typography>
          </Box>
        )}

        {/* What actually HAPPENED. Until 2026-08-19 this stopped at the chip,
            so an owner who approved by email had no way to know whether the
            post went out — one did, at 09:00 the next morning, silently. */}
        {isPublished && draft.postUrl && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Published {new Date(draft.publishedAt).toLocaleDateString(undefined,
              { month: 'short', day: 'numeric' })}
            {' · '}
            <MuiLink href={draft.postUrl} target="_blank" rel="noopener">
              view the post
            </MuiLink>
          </Typography>
        )}

        {isPublished && !draft.postUrl && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Live on your page · {new Date(draft.publishedAt).toLocaleDateString(undefined,
              { month: 'short', day: 'numeric' })}
          </Typography>
        )}

        {!isRejected && !isPublished && !isPendingConnection && draft.scheduledAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Publishing {new Date(draft.scheduledAt).toLocaleString(undefined,
              { weekday: 'short', hour: 'numeric', minute: '2-digit' })}
          </Typography>
        )}

        {draft.publishError && (
          <Typography variant="caption" sx={{ display: 'block', color: '#c62828' }}>
            Could not publish: {draft.publishError}
          </Typography>
        )}

        {draft.imageUrl && (
          <Box
            component="img"
            src={draft.imageUrl}
            alt=""
            loading="lazy"
            sx={{
              width: '100%',
              display: 'block',
              borderRadius: 1.5,
              mt: 1,
              opacity: 0.85,
            }}
          />
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 1,
            fontStyle:       'italic',
            overflow:        'hidden',
            display:         '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {shown.slice(0, 120)}...
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <ArticleIcon sx={{ color: colors.color, fontSize: 20 }} />
        <Chip
          label={draft.label}
          size="small"
          sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 600 }}
        />
        {edited && (
          <Chip
            label="Edited"
            size="small"
            color="info"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>

          {/* The image is part of the artifact being approved, and on instagram it
          IS the post — there is no text-only post type there. Absent from this
          page until 2026-09-18: 83 images were generated and none was ever
          shown here, so every approval from an email link was made on the
          caption alone while the ledger recorded rOwner +1.0 as though the
          whole output had been assessed. The dashboard card has rendered it
          since it was built; this surface never did.

          Image first, caption below — the order the owner will see on the
          platform, and the order that makes a missing image obvious rather
          than easy to scroll past. */}
      {draft.imageUrl && (
        <Box
          component="img"
          src={draft.imageUrl}
          alt=""
          loading="lazy"
          sx={{
            width: '100%',
            display: 'block',
            borderRadius: 1.5,
            mb: 2,
            bgcolor: 'grey.100',
          }}
        />
      )}

      {/* Content — read mode or edit mode */}
      {editing ? (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={5}
            autoFocus
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            disabled={editSaving}
            inputProps={{ maxLength: MAX_CONTENT_LENGTH }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'white' } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {editText.length} characters · saving does not approve this draft
          </Typography>
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p:            1.5,
            mb:           2,
            bgcolor:      'grey.50',
            borderRadius: 1.5,
            maxHeight:    160,
            overflowY:    'auto',
          }}
        >
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
          >
            {shown}
          </Typography>
        </Paper>
      )}

      {/* Errors */}
      {editError && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 1.5 }} onClose={() => setEditError('')}>
          {editError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Edit controls */}
      {editing ? (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={editSaving || !editText.trim()}
            startIcon={editSaving
              ? <CircularProgress size={16} color="inherit" />
              : <SaveIcon />}
            onClick={handleSaveEdit}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            disabled={editSaving}
            onClick={() => { setEditing(false); setEditError(''); }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
          <Button
            size="small"
            variant="text"
            startIcon={<EditIcon />}
            disabled={!!loading || editSaving}
            onClick={() => { setEditText(shown); setEditError(''); setEditing(true); }}
            sx={{ textTransform: 'none' }}
          >
            Edit
          </Button>
          {edited && (
            <Button
              size="small"
              variant="text"
              color="inherit"
              startIcon={editSaving
                ? <CircularProgress size={14} color="inherit" />
                : <RestoreIcon />}
              disabled={!!loading || editSaving}
              onClick={handleRestore}
              sx={{ textTransform: 'none' }}
            >
              Restore original
            </Button>
          )}
        </Box>
      )}

      {/* T2b — said BEFORE the decision, not after it.
          Phrased so approving stays the obvious action: the post is good, it
          simply needs somewhere to go. */}
      {draft.needsConnection && (
        <Box
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1,
            mb: 1.5, px: 1.5, py: 1,
            borderRadius: 1.5,
            bgcolor: '#FFFDF7',
            border: '1px solid',
            borderColor: 'warning.light',
          }}
        >
          <LinkOffIcon sx={{ fontSize: 18, color: 'warning.dark', mt: '2px' }} />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            Your {platformName(draft.platform)} account is not connected yet.
            Approve this and Revfer publishes it as soon as you connect.
          </Typography>
        </Box>
      )}

      {/* Approve / Reject — disabled while the editor is open.
          Approve is NEVER disabled for a missing connection: the approval is
          the M6 signal the learning system runs on, and blocking it would
          leave Reject as the only available action on content the owner
          likes. */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          color="success"
          startIcon={loading === 'approve'
            ? <CircularProgress size={16} color="inherit" />
            : <ThumbUpIcon />}
          disabled={!!loading || editing || editSaving}
          onClick={() => handleAction('approve')}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {loading === 'approve' ? 'Approving...' : 'Approve & Publish'}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={loading === 'reject'
            ? <CircularProgress size={16} color="inherit" />
            : <ThumbDownIcon />}
          disabled={!!loading || editing || editSaving}
          onClick={() => handleAction('reject')}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {loading === 'reject' ? 'Rejecting...' : 'Reject'}
        </Button>
      </Box>
    </Paper>
  );
};

// ===== Main BatchReview Page =====
const BatchReview = () => {
  const { token }               = useParams();
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [data, setData]         = useState(null);
  const [drafts, setDrafts]     = useState([]);
  const [reviewed, setReviewed] = useState(0);
  const [allDone, setAllDone]   = useState(false);

  // ===== Load drafts =====
  useEffect(() => {
    if (!token) {
      setError('Invalid review link — token missing.');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const res = await fetch(
          `${unauthBaseUrl}/v2/drafts/batch/review?token=${token}`,
          { headers: { 'Accept': 'application/json' } }
        );
        const json = await res.json();

        if (!res.ok) {
          throw new Error(json.message || 'Failed to load drafts');
        }

        setData(json);
        setDrafts(json.drafts || []);
        setReviewed(json.reviewed || 0);
        setAllDone(json.allReviewed || false);

      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  // Derived from drafts, so it covers BOTH a draft approved in this session
  // and one approved through an earlier visit that getbatchdrafts returned
  // already in this state.
  const blockedDrafts = useMemo(
    () => drafts.filter(d => d.status === PENDING_CONNECTION),
    [drafts]
  );
  const blockedCount = blockedDrafts.length;

  // Pending drafts that cannot publish where they are aimed. Distinct from
  // blockedDrafts: those were already approved, these have not been decided.
  const unconnectedPending = useMemo(
    () => drafts.filter(d => d.status === 'pending_approval' && d.needsConnection),
    [drafts]
  );

  // One connect action clears every draft in the list, approved or not, so the
  // two groups share a single banner and a single platform set.
  const attentionPlatforms = useMemo(() => [...new Set([
    ...blockedDrafts.map(d => d.platform),
    ...unconnectedPending.map(d => d.platform),
  ].filter(Boolean))], [blockedDrafts, unconnectedPending]);

  // Two destinations. The banner and completion button send someone to connect
  // an account; the completion REDIRECT (clean batch, nothing blocked) just
  // returns them to their business page.
  const connectUrl  = connectAccountsUrl(data?.businessId);
  const finishedUrl = businessUrl(data?.businessId, data?.businessName, data?.businessSlug);

  // ===== Completion redirect =====
  //
  // Fires on the allDone TRANSITION, not from the click handler. Previously
  // the timeout lived inside handleAction, so a page opened on an already
  // finished batch — the common case once an owner revisits an old link —
  // showed "Redirecting to your business page..." and a spinner that never
  // resolved, because no click ever happened. Tabula Medica's 31 Aug link did
  // exactly this.
  //
  // Skipped entirely when something is waiting on a connection: that screen
  // carries an instruction, and navigating away from it is the whole failure
  // this task exists to fix.
  useEffect(() => {
    if (loading || error || !allDone || blockedCount > 0) return;
        const t = setTimeout(() => { window.location.href = finishedUrl; }, REDIRECT_DELAY_MS);
    return () => clearTimeout(t);
  }, [loading, error, allDone, blockedCount, finishedUrl]);

  // ===== Handle approve/reject =====
  const handleAction = useCallback((draftId, status, responseData) => {
    setDrafts(prev => {
      const next = prev.map(d =>
        d.draftId === draftId ? { ...d, status, reviewed: true } : d
      );
      const stillPending = next.filter(d => d.status === 'pending_approval').length;
      if (stillPending === 0 || responseData?.allReviewed) setAllDone(true);
      return next;
    });

    setReviewed(prev => prev + 1);
  }, []);

  // ===== Loading state =====
  if (loading) {
    return (
      <Box sx={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        minHeight:      '100vh',
        gap:            2,
        bgcolor:        '#F3F9FF',
      }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading your drafts...
        </Typography>
      </Box>
    );
  }

  // ===== Error state =====
  if (error) {
    return (
      <Box sx={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        minHeight:      '100vh',
        p:              3,
        bgcolor:        '#F3F9FF',
      }}>
        <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 400, width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} color="error" gutterBottom>
            ⚠️ Invalid Link
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            href={APP_URL}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Go to Revfer
          </Button>
        </Paper>
      </Box>
    );
  }

  // ===== All done state =====
  if (allDone) {
    const hasBlocked = blockedCount > 0;

    return (
      <Box sx={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        minHeight:      '100vh',
        p:              3,
        bgcolor:        '#F3F9FF',
      }}>
        <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 420, width: '100%', textAlign: 'center' }}>
          {hasBlocked ? (
            <>
              <LinkOffIcon sx={{ fontSize: 44, color: 'warning.main', mb: 1.5 }} />
              <Typography variant="h6" fontWeight={700} color="#0D2B6B" gutterBottom>
                One step left
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                You reviewed all {data?.total} drafts. {blockedCount === 1
                  ? '1 approved post cannot'
                  : `${blockedCount} approved posts cannot`} publish until your{' '}
                {platformList(attentionPlatforms)} account is connected.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect it once and Revfer publishes {blockedCount === 1 ? 'it' : 'them'} for
                you. Nothing else is needed.
              </Typography>
              <Button
                variant="contained"
                color="warning"
                href={connectUrl}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Log in and connect {platformList(attentionPlatforms)}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h2" sx={{ mb: 2 }}>🎉</Typography>
              <Typography variant="h5" fontWeight={700} color="success.main" gutterBottom>
                All Done!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                All {data?.total} drafts have been reviewed.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Redirecting to your business page...
              </Typography>
              <CircularProgress size={20} sx={{ mt: 2 }} />
            </>
          )}
        </Paper>
      </Box>
    );
  }

  const pendingCount  = drafts.filter(d => d.status === 'pending_approval' && !d.reviewed).length;
  const progressValue = data?.total ? (reviewed / data.total) * 100 : 0;

  return (
    <Box sx={{ bgcolor: '#F3F9FF', minHeight: '100vh', pb: 4 }}>

      {/* Header */}
      <Box sx={{
        bgcolor:   'white',
        px:        3,
        py:        2.5,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        mb:        3,
        position:  'sticky',
        top:       0,
        zIndex:    10,
      }}>
        <Typography variant="h6" fontWeight={700} color="#0D2B6B">
          📝 Draft Review
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data?.businessName} · {pendingCount} of {data?.total} remaining
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{ mt: 1.5, borderRadius: 1, height: 6 }}
          color="success"
        />
      </Box>

      {/* Draft Cards */}
      <Box sx={{ px: 2, maxWidth: 600, mx: 'auto' }}>

        {/* Approvals that cannot publish yet. Shown above the list because it
            is one action that clears all of them, not a per-draft chore. */}
        {attentionPlatforms.length > 0 && (
          <Alert
            severity="warning"
            icon={<LinkOffIcon fontSize="inherit" />}
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              <Button
                size="small"
                color="warning"
                variant="contained"
                href={connectUrl}
                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Connect
              </Button>
            }
          >
            <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
              {blockedCount > 0
                ? (blockedCount === 1
                    ? '1 approved post is waiting'
                    : `${blockedCount} approved posts are waiting`)
                : `Your ${platformList(attentionPlatforms)} account is not connected`}
            </AlertTitle>
            {blockedCount > 0
              ? <>Connect your {platformList(attentionPlatforms)} account and Revfer
                  publishes{blockedCount === 1 ? ' it' : ' them'} for you.</>
              : <>Approve whatever you like — Revfer publishes each post as soon as you
                  connect.</>}
          </Alert>
        )}

        {/* Approving from an email link never chooses a time —
            approvedraftbytoken calls scheduleDraftForPublish with no options,
            so it takes the platform's optimal window. Nothing said so, and a
            post approved on 2026-08-19 published at 09:00 the next morning
            with no indication to the owner that it had. */}
        {pendingCount > 0 && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Approved content publishes automatically at the best time for each
            platform — usually within a day or two. To pick your own time,
            approve from your{' '}
            <MuiLink href={`${window.location.origin}/drafts`} underline="always">
              Revfer dashboard
            </MuiLink>{' '}
            instead.
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {drafts.map((draft, index) => (
            <Box key={draft.draftId}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
              >
                {index + 1} of {data?.total}
              </Typography>
              <DraftCard
                draft={draft}
                token={token}
                businessId={data?.businessId}
                businessName={data?.businessName}
                businessSlug={data?.businessSlug}
                onAction={handleAction}
              />
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Divider sx={{ my: 3 }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center' }}
        >
          Powered by Revfer · Reply STOP to stop SMS notifications
        </Typography>
      </Box>
    </Box>
  );
};

export default BatchReview;